import { getNativeBilling, isNativeStoreApp, STORE_PRODUCT_IDS, storeLabel } from "@/lib/store-billing";
import { purchasePremiumViaStore } from "@/lib/native-billing";
import { createCheckoutSession } from "@/utils/payments.functions";
import { PREMIUM_MONTHLY_PRICE, TRIAL_DAYS } from "@/lib/premium";
import { track } from "@/lib/analytics";

/**
 * Single source of truth for the paid-trial flow. A Premium trial is always
 * attached to the recurring monthly subscription and therefore always requires
 * a payment method: Stripe Checkout on web, StoreKit / Play Billing inside the
 * native wrappers. There is no card-free grant path.
 */
export const MONTHLY_PLAN_KEY = "inquira_pro_monthly" as const;
export const MONTHLY_STORE_PRODUCT_ID = STORE_PRODUCT_IDS.monthly;

export const TRIAL_HEADLINE = `${TRIAL_DAYS}-Day Free Trial`;
export const TRIAL_SUBHEAD = `Then ${PREMIUM_MONTHLY_PRICE}/month`;
export const TRIAL_CTA = `Start ${TRIAL_DAYS}-Day Free Trial`;
export const TRIAL_DISCLOSURE = `Then ${PREMIUM_MONTHLY_PRICE}/month • Auto-renews until canceled • Cancel anytime`;
export const TRIAL_CANCEL_NOTE = `Cancel before your free trial ends to avoid the first ${PREMIUM_MONTHLY_PRICE} monthly renewal charge.`;
export const CANCEL_ANYTIME = "Cancel anytime.";
/** No-trial offer copy, used when the store reports the customer is ineligible. */
export const NO_TRIAL_CTA = `Subscribe – ${PREMIUM_MONTHLY_PRICE}/month`;
export const NO_TRIAL_DISCLOSURE = `${PREMIUM_MONTHLY_PRICE}/month • Auto-renews until canceled • Cancel anytime`;
/** Apple-required auto-renewable subscription disclosure. */
export const APPLE_DISCLOSURE =
  "Payment will be charged to your Apple Account at confirmation of purchase. The subscription automatically renews unless canceled at least 24 hours before the end of the current subscription period. You can manage or cancel your subscription through your Apple Account settings.";
export const TRIAL_TERMS = `Start your free trial today. A valid payment method is required. After your ${TRIAL_DAYS}-day trial ends, your subscription automatically renews at ${PREMIUM_MONTHLY_PRICE}/month unless canceled before the trial ends. You can cancel anytime before the trial ends to avoid being charged.`;

export function trialChannelLabel(): string {
  if (!isNativeStoreApp()) return "Stripe";
  const android = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
  return storeLabel(android ? "google" : "apple");
}

export type TrialStartOutcome =
  | { ok: true; channel: "store"; redirecting: false }
  | { ok: true; channel: "web"; redirecting: true }
  | { ok: false; error: "cancelled" | "store_not_configured" | "checkout_failed" | "failed" };

/**
 * Starts the trial by creating the real recurring subscription with its
 * introductory free period. Premium only turns on once the billing platform
 * confirms the subscription (Stripe webhook / verified store receipt).
 */
export async function startTrialWithPaymentMethod(surface: string): Promise<TrialStartOutcome> {
  void track("trial_checkout_started", { surface, days: TRIAL_DAYS, native: isNativeStoreApp() });

  if (getNativeBilling()) {
    // Apple / Google own the payment method and the introductory offer.
    const result = await purchasePremiumViaStore("monthly");
    if (result.ok) return { ok: true, channel: "store", redirecting: false };
    return {
      ok: false,
      error:
        result.error === "cancelled"
          ? "cancelled"
          : result.error === "store_not_configured" || result.error === "no_bridge"
            ? "store_not_configured"
            : "failed",
    };
  }

  try {
    const result = await createCheckoutSession({
      data: { priceId: MONTHLY_PLAN_KEY, origin: window.location.origin, withTrial: true },
    });
    if (!result?.url) return { ok: false, error: "checkout_failed" };
    window.location.href = result.url;
    return { ok: true, channel: "web", redirecting: true };
  } catch (err) {
    console.error("Trial checkout failed:", err);
    return { ok: false, error: "checkout_failed" };
  }
}
